import { AppLink } from '@/components/links/AppLink';
import { Logo } from '@/components/media/Logo';
import { useManifest } from '@/contexts/ManifestContext';
import { BodyMedium, BodySmall, Heading4 } from '@/layout/Typography';
import { Routes } from '@/routes';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface NavLinkProps {
  route: string;
  external: boolean;
  translationKey: string;
}

const NavLink: React.FC<NavLinkProps> = ({
  route,
  external,
  translationKey,
}) => {
  const { t } = useTranslation();

  const className = 'hover:opacity-80';

  return (
    <BodyMedium>
      {external ? (
        <a key={translationKey} href={route} className={className}>
          {t(translationKey)}
        </a>
      ) : (
        <AppLink key={translationKey} href={route} className={className}>
          {t(translationKey)}
        </AppLink>
      )}
    </BodyMedium>
  );
};

interface PolicyLinkProps {
  route: string;
  translationKey: string;
}

const PolicyLink: React.FC<PolicyLinkProps> = ({ route, translationKey }) => {
  const { t } = useTranslation();

  return (
    <BodyMedium key={translationKey} className="font-bold">
      <AppLink
        href={route}
        className="text-neutral-white underline decoration-2 underline-offset-8 hover:no-underline"
      >
        {t(translationKey)}
      </AppLink>
    </BodyMedium>
  );
};

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { footer, type, supportUrl, mainSiteUrl, features } = useManifest();

  const navItems = [
    {
      route: Routes.Impact,
      external: false,
      translationKey: 'layout.footer.impact',
    },
    {
      route: Routes.Projects,
      external: false,
      translationKey: 'layout.footer.projects',
    },
    {
      route: Routes.Membership,
      external: false,
      translationKey: 'layout.footer.membership',
    },
    {
      route: Routes.Supporters,
      external: false,
      translationKey: 'layout.footer.supporters',
    },
    {
      route: `https://resources.sumthing.org/about`,
      external: true,
      translationKey: 'layout.footer.about',
    },
    {
      route: Routes.Business,
      external: false,
      translationKey: 'layout.footer.for-business',
    },
    {
      route: Routes.Connect,
      external: false,
      translationKey: 'layout.footer.for-projects',
    },
  ];

  const policyItems = [
    {
      route: Routes.Terms,
      translationKey: 'layout.menu.terms',
    },
    {
      route: Routes.Privacy,
      translationKey: 'layout.menu.privacy',
    },
    {
      route: Routes.Cookies,
      translationKey: 'layout.menu.cookies',
    },
  ];

  if (type === 'partner') {
    return (
      <footer className="border-primary bg-neutral-white text-primary border-t px-6 py-8">
        {/* Row 1 */}
        <div className="mx-auto flex max-w-screen-2xl flex-col gap-6 md:flex-row md:items-center md:justify-between">
          {/* Left: Logo */}
          <div className="flex items-center">
            <AppLink
              href={mainSiteUrl ?? Routes.Home}
              className="group inline-block"
            >
              <Logo src={footer.logo.url} height={footer.logo.height} />
              <span></span>
            </AppLink>
          </div>

          {/* Right: Links */}
          {features.showFooterSiteLinks !== false && (
            <nav
              aria-label="Footer navigation"
              className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:justify-end md:gap-x-6 md:gap-y-3"
            >
              <BodySmall className="font-bold">
                <a
                  href={mainSiteUrl}
                  className="text-primary no-underline decoration-2 underline-offset-4 hover:underline"
                >
                  {t('layout.menu.mainSite')}
                </a>
              </BodySmall>
              <BodySmall className="font-bold">
                <a
                  href={supportUrl}
                  className="text-primary no-underline decoration-2 underline-offset-4 hover:underline"
                >
                  {t('layout.menu.contactSupport')}
                </a>
              </BodySmall>
              <BodySmall className="font-bold">
                <AppLink
                  href={Routes.Projects}
                  className="text-primary no-underline decoration-2 underline-offset-4 hover:underline"
                >
                  {t('layout.menu.projects')}
                </AppLink>
              </BodySmall>
              <BodySmall className="font-bold">
                <AppLink
                  href={Routes.Impact}
                  className="text-primary no-underline decoration-2 underline-offset-4 hover:underline"
                >
                  {t('layout.menu.impact')}
                </AppLink>
              </BodySmall>
              <BodySmall className="font-bold">
                <AppLink
                  href={Routes.Supporters}
                  className="text-primary no-underline decoration-2 underline-offset-4 hover:underline"
                >
                  {t('layout.menu.supporters')}
                </AppLink>
              </BodySmall>
            </nav>
          )}
        </div>

        {/* Row 2 */}
        <div className="mx-auto mt-6 max-w-screen-2xl">
          <hr className="border-neutral-medium" />
          <div className="pt-8">
            <BodyMedium className="text-primary">
              {t('layout.menu.poweredBy')}{' '}
              <a
                href="https://www.sumthing.org"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary underline decoration-2 underline-offset-6 hover:no-underline"
              >
                Sumthing.org
              </a>
            </BodyMedium>
          </div>
        </div>
      </footer>
    );
  }

  // Sumthing footer (default)
  return (
    <footer className="bg-primary-alt text-neutral-white 3xl:px-32 px-6 py-12 xl:px-24">
      <div className="mx-auto grid max-w-screen-2xl grid-cols-1 items-start gap-12 md:grid-cols-5">
        <div className="order-3 space-y-6 md:order-1 md:col-span-1">
          <AppLink
            href={mainSiteUrl ?? Routes.Home}
            className="group inline-block"
          >
            <Logo src={footer.logo.url} height={footer.logo.height} />
            <span>
              {/* an empty span is necessary to force on hover effects */}
            </span>
          </AppLink>
          <div className="space-y-6">
            <BodyMedium>
              <a href={supportUrl} className="text-neutral-white underline">
                info@sumthing.org
              </a>
            </BodyMedium>
            <BodyMedium>
              <a
                href="https://maps.app.goo.gl/b9bryc581u7sRp626"
                target="_blank"
                rel="noopener noreferrer"
                className="text-neutral-white block"
              >
                {'Plantage Middenlaan 2C'}
                <br />
                {'1018 DD Amsterdam'}
              </a>
            </BodyMedium>
          </div>
        </div>

        <div className="order-2 space-y-4 text-left md:col-span-3 md:mx-auto">
          <Heading4 className="text-accent-1">
            {t('layout.newsletter.line1')}
            <br />
            {t('layout.newsletter.line2')}
          </Heading4>

          <div className="klaviyo-form-UUL9zz bg-neutral-white mt-4 max-w-[400px] rounded-full"></div>
        </div>

        <div className="order-1 flex justify-start md:order-3 md:col-span-1 md:justify-end">
          <nav className="flex flex-col gap-4">
            {navItems.map(({ translationKey, external, route }) => (
              <NavLink
                key={translationKey}
                route={route}
                external={external}
                translationKey={translationKey}
              />
            ))}
          </nav>
        </div>
      </div>

      <div className="mt-12 pt-6">
        <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:justify-center md:gap-6 md:text-center">
          <BodyMedium className="text-neutral-white font-bold">
            &copy; {new Date().getFullYear()} Sumthing
          </BodyMedium>
          {policyItems.map(({ translationKey, route }) => (
            <PolicyLink route={route} translationKey={translationKey} />
          ))}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
